import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Alert,
} from "@material-tailwind/react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { fetcherFull } from "utils/api";
import { useRateLimit, sanitiseInput } from "utils/utils.jsx";
import { totpField } from "utils/validation";
import { toast } from "react-toastify";
import RHFInput from "components/form/rhf-input";

// Lives inside the <Dialog> so every open remounts the form and clears the
// code (recipe P5): RHF's store would otherwise survive close/reopen
// with a stale draft.
const TotpVerifyForm = ({
    customVerifyFunction,
    isBlocked,
    incrementAttempts,
    resetAttempts,
    onVerifySuccess,
    onClose,
}) => {
    const validationSchema = Yup.object().shape({
        totp: totpField.required("2FA token is required"),
    });

    const defaultVerifyTOTP = async (totp) => {
        const sanitisedTotp = sanitiseInput(totp?.replace(/\s+/g, ""));
        return fetcherFull("/v2/auth/totp", "POST", { code: sanitisedTotp });
    };

    const methods = useForm({
        mode: "onChange",
        resolver: yupResolver(validationSchema),
        defaultValues: { totp: "" },
    });
    // Destructure the formState flags up front (P1): formState is a
    // subscription proxy and conditional reads drop subscriptions.
    const { isValid, isDirty, isSubmitting } = methods.formState;

    const handleSubmit = async (values) => {
        if (isBlocked) {
            toast.error("Too many attempts. Please try again later.");
            return;
        }
        try {
            const verifyFunction = customVerifyFunction || defaultVerifyTOTP;
            // RAW value: defaultVerifyTOTP sanitises internally, and the
            // same untouched value rides on to onVerifySuccess.
            await verifyFunction(values.totp);
            resetAttempts();
            onVerifySuccess(values.totp);
            onClose();
        } catch (error) {
            incrementAttempts();
            toast.error("Invalid 2FA token or network error. Please try again.");
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
                <DialogBody className="dialog__body">
                    <div className="font-body py-4">
                        <p className="text-primary">
                            Enter the passcode from your authentication application
                        </p>
                        {isBlocked && (
                            <Alert color="red" className="mt-2">
                                Too many failed attempts. Please try again later.
                            </Alert>
                        )}
                        <RHFInput
                            autoComplete="one-time-code"
                            className="font-display text-lg bg-white pt-2"
                            name="totp"
                            type="text"
                            color="black"
                            label="Authentication Code"
                            required
                        />
                    </div>
                </DialogBody>
                <DialogFooter className="dialog__footer pt-0">
                    <div>
                        <Button
                            type="button"
                            className="btn btn--primary mx-2"
                            variant="filled"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="btn btn--primary"
                            variant="filled"
                            disabled={!(isValid && isDirty) || isSubmitting || isBlocked}
                        >
                            {isSubmitting ? "Verifying..." : "Verify"}
                        </Button>
                    </div>
                </DialogFooter>
            </form>
        </FormProvider>
    );
};

const TotpVerifyDialog = ({
    open,
    onClose,
    onVerifySuccess,
    customVerifyFunction = null
}) => {
    const { isBlocked, incrementAttempts, resetAttempts } = useRateLimit();

    return (
        <Dialog
            open={open}
            handler={onClose}
            className="dialog"
        >
            <div className="bg-white rounded-xl">
                <DialogHeader className="dialog__header">
                    Verify Your Identity
                </DialogHeader>
                <TotpVerifyForm
                    customVerifyFunction={customVerifyFunction}
                    isBlocked={isBlocked}
                    incrementAttempts={incrementAttempts}
                    resetAttempts={resetAttempts}
                    onVerifySuccess={onVerifySuccess}
                    onClose={onClose}
                />
            </div>
        </Dialog>
    );
};

export default TotpVerifyDialog;
